import type { ReactNode } from 'react';
import { useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useInvalidatingMutation, useSettingsQuery } from '../../hooks';
import type { FeedbackSeverity } from '../../lib/useFeedback';
import type {
  ProviderSummary,
  ProviderUpsertRequest,
  TestConnectionResult,
} from '../../model/settingsTypes';

import { ProviderFormDialog } from './ProviderFormDialog';
import { SettingsCard } from './SettingsCard';
import { TestConnectionButton } from './TestConnectionButton';

export interface ProviderApi {
  list: () => Promise<ProviderSummary[]>;
  create: (body: ProviderUpsertRequest) => Promise<ProviderSummary>;
  update: (providerId: string, body: ProviderUpsertRequest) => Promise<ProviderSummary>;
  remove: (providerId: string) => Promise<void>;
  test: (providerId: string) => Promise<TestConnectionResult>;
}

export interface ProviderManagerProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  listKey: QueryKey;
  api: ProviderApi;
  canManage: boolean;
  canManageCredentials: boolean;
  credentialHint?: string;
  onFeedback: (severity: FeedbackSeverity, message: string) => void;
}

function sortProviders(providers: ProviderSummary[]): ProviderSummary[] {
  return [...providers].sort((a, b) => {
    const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
    const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
    if (pa !== pb) {
      return pa - pb;
    }
    return a.name.localeCompare(b.name);
  });
}

/**
 * Full CRUD surface for the provider sub-resources shared by the delivery,
 * payment, and notification domains (the three expose an identical contract).
 * Wires list / create / update / delete / test-connection to the UI.
 */
export function ProviderManager({
  title,
  description,
  icon,
  listKey,
  api,
  canManage,
  canManageCredentials,
  credentialHint,
  onFeedback,
}: ProviderManagerProps) {
  const { t } = useTranslation('settings');
  const query = useSettingsQuery<ProviderSummary[]>(listKey, api.list);
  const createMutation = useInvalidatingMutation<ProviderSummary, ProviderUpsertRequest>(
    listKey,
    api.create,
  );
  const updateMutation = useInvalidatingMutation<
    ProviderSummary,
    { id: string; body: ProviderUpsertRequest }
  >(listKey, ({ id, body }) => api.update(id, body));
  const deleteMutation = useInvalidatingMutation<void, string>(listKey, api.remove);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<ProviderSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProviderSummary | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestConnectionResult>>({});

  const openCreate = () => {
    setDialogMode('create');
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (provider: ProviderSummary) => {
    setDialogMode('edit');
    setEditing(provider);
    setDialogOpen(true);
  };

  const handleSubmit = (body: ProviderUpsertRequest) => {
    if (dialogMode === 'create') {
      createMutation.mutate(body, {
        onSuccess: () => {
          setDialogOpen(false);
          onFeedback('success', t('providerManager.createdFeedback', { name: body.name }));
        },
        onError: (error) => onFeedback('error', error.message),
      });
    } else if (editing) {
      updateMutation.mutate(
        { id: editing.id, body },
        {
          onSuccess: () => {
            setDialogOpen(false);
            onFeedback('success', t('providerManager.updatedFeedback', { name: body.name }));
          },
          onError: (error) => onFeedback('error', error.message),
        },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) {
      return;
    }
    const target = deleteTarget;
    deleteMutation.mutate(target.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        onFeedback('success', t('providerManager.deletedFeedback', { name: target.name }));
      },
      onError: (error) => onFeedback('error', error.message),
    });
  };

  const providers = query.data ? sortProviders(query.data) : [];
  const saving = createMutation.isPending || updateMutation.isPending;

  const action =
    canManage && query.isSuccess && providers.length > 0 ? (
      <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={openCreate}>
        {t('providerManager.addProvider')}
      </Button>
    ) : undefined;

  return (
    <SettingsCard
      title={title}
      description={description}
      icon={icon}
      action={action}
      divided={false}
    >
      <Box sx={{ pt: 1 }}>
        {query.isLoading ? (
          <LoadingState label={t('providerManager.loading')} fullHeight={false} />
        ) : query.isError ? (
          <ErrorState
            title={t('providerManager.loadError')}
            message={query.error?.message ?? t('common:error.unknown')}
            onRetry={() => void query.refetch()}
          />
        ) : providers.length === 0 ? (
          <EmptyState
            title={t('providerManager.emptyTitle')}
            description={t('providerManager.emptyDescription')}
            action={
              canManage ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  {t('providerManager.addProvider')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('providerManager.table.provider')}</TableCell>
                  <TableCell>{t('providerManager.table.mode')}</TableCell>
                  <TableCell align="center">{t('providerManager.table.priority')}</TableCell>
                  <TableCell>{t('providerManager.table.credential')}</TableCell>
                  <TableCell>{t('providerManager.table.connection')}</TableCell>
                  <TableCell align="right">{t('providerManager.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.map((provider) => {
                  const result = testResults[provider.id];
                  return (
                    <TableRow key={provider.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {provider.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {provider.code}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={provider.enabled ? 'success' : 'default'}
                            label={
                              provider.enabled
                                ? t('providerManager.enabledChip')
                                : t('providerManager.disabledChip')
                            }
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={provider.mode === 'PRODUCTION' ? 'primary' : 'default'}
                          variant={provider.mode === 'PRODUCTION' ? 'filled' : 'outlined'}
                          label={
                            provider.mode === 'PRODUCTION'
                              ? t('providerManager.productionChip')
                              : t('providerManager.sandboxChip')
                          }
                        />
                      </TableCell>
                      <TableCell align="center">{provider.priority ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={provider.credential?.present ? 'success' : 'default'}
                          label={
                            provider.credential?.present
                              ? t('providerManager.credentialSetChip')
                              : t('providerManager.credentialNoneChip')
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {result ? (
                          <Tooltip title={result.message}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={result.success ? 'success' : 'warning'}
                              label={
                                result.success
                                  ? t('providerManager.reachableChip')
                                  : t('providerManager.unreachableChip')
                              }
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            {t('providerManager.notTested')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <TestConnectionButton
                            compact
                            label={t('providerManager.testButtonLabel')}
                            run={() => api.test(provider.id)}
                            onResult={(testResult) => {
                              setTestResults((prev) => ({ ...prev, [provider.id]: testResult }));
                              onFeedback(
                                testResult.success ? 'success' : 'warning',
                                t('providerManager.testResultFeedback', {
                                  name: provider.name,
                                  message: testResult.message,
                                }),
                              );
                            }}
                          />
                          <Tooltip title={canManage ? t('common:action.edit') : t('providerManager.readOnlyTooltip')}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={t('providerManager.editAria', { name: provider.name })}
                                disabled={!canManage}
                                onClick={() => openEdit(provider)}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canManage ? t('common:action.delete') : t('providerManager.readOnlyTooltip')}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={t('providerManager.deleteAria', { name: provider.name })}
                                disabled={!canManage}
                                onClick={() => setDeleteTarget(provider)}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ProviderFormDialog
        open={dialogOpen}
        mode={dialogMode}
        initial={editing}
        canManageCredentials={canManageCredentials}
        credentialHint={credentialHint}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('providerManager.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('providerManager.deleteDialogBody', { name: deleteTarget?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineRoundedIcon />
              )
            }
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsCard>
  );
}
